import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api';
import type {
  CreatePendingPaymentRequest,
  ErrorMessage,
  InitiatePendingPaymentResponse,
  PayPendingPaymentRequest,
} from '../../../generated-api';
import { parseError } from '../../utils/error-handler';
import { useLocation } from '../../context/LocationContext';

export const useMutateCreatePendingPayment = () => {
  const apiClient = useApiClient();

  const postInitiatePayment = (requestParameters: CreatePendingPaymentRequest) =>
    apiClient.PendingPaymentControllerApi.createPendingPayment(requestParameters).catch(parseError);

  return useMutation<InitiatePendingPaymentResponse, ErrorMessage, CreatePendingPaymentRequest>({
    mutationFn: postInitiatePayment,
    mutationKey: ['create-pending-payment'],
    throwOnError: true,
  });
};

export const usePendingPaymentPolling = (paymentMethodId: string) => {
  const apiClient = useApiClient();
  const { location } = useLocation();
  const { data, refetch, isSuccess } = useQuery({
    queryKey: ['paymentMethodId', paymentMethodId],
    queryFn: async () =>
      apiClient.PendingPaymentControllerApi.poll({ locationId: location.locationId, paymentMethodId }),
    retry: true,
    retryDelay: 1000,
    // RefetchOnWindowFocus: false,
  });
  return { data, isSuccess, refetch };
};

export const usePayPendingPayment = () => {
  const apiClient = useApiClient();
  return useMutation({
    mutationFn: async (requestParameters: PayPendingPaymentRequest) =>
      apiClient.PendingPaymentControllerApi.payPendingPayment(requestParameters).catch(parseError),
    mutationKey: ['pay-pending-payment'],
    throwOnError: true,
  });
};

export const usePendingPaymentStatePolling = (
  pendingPaymentId: number | undefined,
  paymentMethodId: string | undefined
) => {
  const apiClient = useApiClient();
  const { location } = useLocation();

  const { data, refetch } = useQuery({
    queryFn: async () => {
      if (!pendingPaymentId || !paymentMethodId) {
        return null;
      }

      return apiClient.PendingPaymentControllerApi.getPendingPaymentState({
        locationId: location.locationId,
        paymentMethodId,
        pendingPaymentId,
      });
    },
    queryKey: ['pendingPaymentState', pendingPaymentId],
  });

  return { data, refetch };
};
